import { getAPIBase } from "./config";
import { PEOPLE_TO_SEE, THINGS_TO_AVOID, THINGS_TO_DO, THINGS_TO_TAKE } from "./constants";

export const REGENERUS_ABOUT_URL = "https://www.regeneruslabs.me/about";
export const REGENERUS_PATIENT_PORTAL_URL = "https://www.regeneruslabs.me/patient-portal";
export const REGENERUS_PRACTITIONER_PORTAL_URL = "https://www.regeneruslabs.me/practitioner-portal";
export const REGENERUS_CONTACT_URL = "https://www.regeneruslabs.me/contact";
export const REGENERUS_VACANCIES_URL = "https://regeneruslabs.livevacancies.co.uk/#/";

// External URLs
export const MARKETING_URL = "https://www.regeneruslabs.com/";
export const HOW_IT_WORKS_URL = "https://www.regeneruslabs.com/how-it-works";
export const OUR_MISSION_URL = "https://www.regeneruslabs.com/about#mission";
export const THE_SCIENCE_URL = "https://www.regeneruslabs.com/about#science";
export const MEET_THE_TEAM_URL = "https://www.regeneruslabs.com/about#team";
export const CONTACT_US_URL = "mailto:info@regeneruslabs.com";
export const FOR_RETAILERS_URL = "https://www.regeneruslabs.com/partners#business";
export const PARTNERS_EXTERNAL_URL = "https://www.regeneruslabs.me/";

// Slugs
export const OVERVIEW_SLUG = "overview";
export const RECOMMENDATIONS_SLUG = "recommendations";
export const RESULTS_SLUG = "results";
export const SCORES_SLUG = "scores";
export const CORE_SECTOR_SUBPATHS = [
  OVERVIEW_SLUG,
  SCORES_SLUG,
  RECOMMENDATIONS_SLUG,
  RESULTS_SLUG
];

export const COOKIE_SLUG = "cookie-policy";
export const PRIVACY_SLUG = "privacy-policy";
export const TERMS_OF_SERVICE_SLUG = "terms-of-service";
export const TERMS_OF_USE_SLUG = "terms-of-use";
export const PARTNER_TERMS_SLUG = "partner-terms-and-conditions";
export const SHIPPING_AND_RETURNS_SLUG = "shipping-and-returns-policy";

export const REGENERUS_TERMS_AND_CONDITIONS_SLUG = "regenerus-terms-and-conditions";
export const REGENERUS_PRIVACY_SLUG = "regenerus-privacy-policy";
export const REGENERUS_SHIPPING_SLUG = "regenerus-shipping-policy";
export const REGENERUS_TERMS_OF_USE_SLUG = "regenerus-terms-of-use";
export const REGENERUS_COOKIE_POLICY_SLUG = "regenerus-cookie-policy";

// Core
export const HOME_URL = "/";
export const DASHBOARD_HOME_URL = "/dashboard";
export const FOODS_URL = "/food-list";
export const SETTINGS_URL = "/settings";
export const PROFILE_URL = "/profile";
export const EXPERTS_ROOT_URL = "/find-a-practitioner";
export const EXPERTS_OVERVIEW_URL = `${EXPERTS_ROOT_URL}/overview`;
export const EXPERTS_URL = `${EXPERTS_ROOT_URL}/experts`;
export const EXPERTS_DETAIL_URL = `${EXPERTS_URL}/:id`;
export const EXPERTS_SUPPORTED_TESTS_URL = `${EXPERTS_ROOT_URL}/tests`;
export const EXPERTS_SUPPORTED_TEST_URL = `${EXPERTS_ROOT_URL}/tests/:slug`;

export const getExpertsDetailUrl = expertId => EXPERTS_DETAIL_URL.replace(":id", expertId);

// Results
export const RESULTS_URL_BASE = "/results";
export const RESULTS_PATTERN_URL = `${RESULTS_URL_BASE}/:sectorSlug/:slug`;
export function getResultUrlFromSlugs(sectorSlug, slug) {
  return RESULTS_PATTERN_URL.replace(":sectorSlug", sectorSlug).replace(":slug", slug);
}
// Non-Sector Based Results
export const NSB_RESULTS_PATTERN_URL = `${RESULTS_URL_BASE}/:testCode`;
export function getResultUrlFromTest(testInput) {
  return NSB_RESULTS_PATTERN_URL.replace(":testCode", testInput.id);
}

// Recommendations slugs
export const THINGS_TO_DO_SLUG = "things-to-do";
export const THINGS_TO_AVOID_SLUG = "things-to-avoid";
export const THINGS_TO_TAKE_SLUG = "supplements";
export const PEOPLE_TO_SEE_SLUG = "people-to-see";

// Things to do
export const THINGS_TO_DO_URL_BASE = "/things-to-do";
export const THING_TO_DO_PATTERN_URL = "/things-to-do/:slug";
export function getThingToDoUrl(slug) {
  return THING_TO_DO_PATTERN_URL.replace(":slug", slug);
}

// Things to avoid
export const THINGS_TO_AVOID_URL_BASE = "/things-to-avoid";
export const THING_TO_AVOID_PATTERN_URL = "/things-to-avoid/:slug";
export function getThingToAvoidUrl(slug) {
  return THING_TO_AVOID_PATTERN_URL.replace(":slug", slug);
}

// Things to take
export const SUPPLEMENTS_URL = "/supplements";
export const SUPPLEMENT_PATTERN_URL = "/supplements/:slug";
export function getSupplementUrl(slug) {
  return SUPPLEMENT_PATTERN_URL.replace(":slug", slug);
}

// People to see
export const PEOPLE_TO_SEE_URL_BASE = "/people-to-see";
export const PERSON_TO_SEE_PATTERN_URL = "/people-to-see/:slug";
export function getPersonToSeeUrl(slug) {
  return PERSON_TO_SEE_PATTERN_URL.replace(":slug", slug);
}

export const RECOMMENDATION_PATTERN_URL = "/:recommendationTypeSlug/:slug";
export function getRecommendationUrl(recommendation) {
  const recommendationTypeSlug = getRecommendationTypeSlug(recommendation);
  return RECOMMENDATION_PATTERN_URL.replace(
    ":recommendationTypeSlug",
    recommendationTypeSlug
  ).replace(":slug", recommendation.slug);
}

export function getRecommendationTypeSlug(recommendation) {
  if (recommendation.recommendationType === THINGS_TO_DO) return THINGS_TO_DO_SLUG;
  if (recommendation.recommendationType === THINGS_TO_AVOID) return THINGS_TO_AVOID_SLUG;
  if (recommendation.recommendationType === THINGS_TO_TAKE) return THINGS_TO_TAKE_SLUG;
  if (recommendation.recommendationType === PEOPLE_TO_SEE) return PEOPLE_TO_SEE_SLUG;
  console.error("Unrecognised recommendationType : ", recommendation.recommendationType);
}

// Questionnaires
export const QUESTIONNAIRES_URL_BASE = "/questionnaires";
export const QUESTIONNAIRE_INTRO_PATTERN_URL = `${QUESTIONNAIRES_URL_BASE}/:slug/intro`;
export function getQuestionnaireIntroUrl(slug) {
  return QUESTIONNAIRE_INTRO_PATTERN_URL.replace(":slug", slug);
}
export const QUESTIONNAIRE_SUBMIT_PATTERN_URL = `${QUESTIONNAIRES_URL_BASE}/:slug/submit`;
export function getQuestionnaireSubmitUrl(slug) {
  return QUESTIONNAIRE_SUBMIT_PATTERN_URL.replace(":slug", slug);
}

export const TEST_PRODUCT_QUESTIONNAIRE_INTRO_URL = `${QUESTIONNAIRES_URL_BASE}/:testProductSlug/:questionnaireSlug/intro`;
export function getTestProductQuestionnaireIntroUrl(testProductSlug, questionnaireSlug) {
  return TEST_PRODUCT_QUESTIONNAIRE_INTRO_URL.replace(":testProductSlug", testProductSlug).replace(
    ":questionnaireSlug",
    questionnaireSlug
  );
}

export const TEST_PRODUCT_QUESTIONNAIRE_STEP_PATTERN_URL = `${QUESTIONNAIRES_URL_BASE}/:testProductSlug/:questionnaireSlug/questions`;
export function getTestProductQuestionnaireStepUrl(testProductSlug, questionnaireSlug) {
  return TEST_PRODUCT_QUESTIONNAIRE_STEP_PATTERN_URL.replace(
    ":testProductSlug",
    testProductSlug
  ).replace(":questionnaireSlug", questionnaireSlug);
}

export const TEST_PRODUCT_QUESTIONNAIRE_QUESTIONS_PATTERN_URL = `${QUESTIONNAIRES_URL_BASE}/:testProductSlug/:questionnaireSlug/questions`;
export function getTestProductQuestionnaireQuestionsUrl(testProductSlug, questionnaireSlug) {
  return TEST_PRODUCT_QUESTIONNAIRE_QUESTIONS_PATTERN_URL.replace(
    ":testProductSlug",
    testProductSlug
  ).replace(":questionnaireSlug", questionnaireSlug);
}

export const TEST_PRODUCT_QUESTIONNAIRE_OUTRO_URL = `${QUESTIONNAIRES_URL_BASE}/:testProductSlug/:questionnaireSlug/outro`;
export function getTestProductQuestionnaireOutroUrl(testProductSlug, questionnaireSlug) {
  return TEST_PRODUCT_QUESTIONNAIRE_OUTRO_URL.replace(":testProductSlug", testProductSlug).replace(
    ":questionnaireSlug",
    questionnaireSlug
  );
}

export const QUESTIONNAIRE_STEP_PATTERN_URL = `${QUESTIONNAIRES_URL_BASE}/:slug/:step`;
export function getQuestionnaireStepUrl(slug, step) {
  return QUESTIONNAIRE_STEP_PATTERN_URL.replace(":slug", slug).replace(":step", step);
}

export const QUESTIONNAIRE_OUTRO_PATTERN_URL = `${QUESTIONNAIRES_URL_BASE}/:slug/outro`;
export function getQuestionnaireOutroUrl(slug) {
  return QUESTIONNAIRE_OUTRO_PATTERN_URL.replace(":slug", slug);
}

// Orders
export const PATIENT_CHECKOUT_URL = "/payment/checkout/:orderId";
export const ORDERS_URL = "/orders";
export const ORDER_PATTERN_URL = "/orders/:orderId";
export function getOrderUrl(orderId) {
  return ORDER_PATTERN_URL.replace(":orderId", orderId);
}
export const ORDER_PROCESSING_PATTERN_URL = "/orders/:orderId/processing";
export function getOrderProcessingUrl(orderId) {
  return ORDER_PROCESSING_PATTERN_URL.replace(":orderId", orderId);
}

// Sectors
export const SECTOR_URL_BASE = "/sectors";
export const SECTOR_PATTERN_URL = `${SECTOR_URL_BASE}/:sectorSlug/:slug`;
export function getSectorUrl(sectorSlug, slug) {
  return SECTOR_PATTERN_URL.replace(":sectorSlug", sectorSlug).replace(":slug", slug);
}
export function getSectorUrlPattern(slug) {
  return `${SECTOR_URL_BASE}/:slug/${slug}`;
}

// Tests

export const SHOP_URL = "/shop";
export const SHOP_TESTS_URL = `${SHOP_URL}/tests`;
export const SHOP_TEST_PRODUCT_PATTERN_URL = "/shop/tests/:slug";
export function getShopTestProductUrl(slug) {
  return SHOP_TEST_PRODUCT_PATTERN_URL.replace(":slug", slug);
}
export const SHOP_REPORTS_URL = `${SHOP_URL}/reports`;
export const SHOP_REPORT_PRODUCT_PATTERN_URL = "/shop/reports/:slug";
export function getShopReportProductUrl(slug) {
  return SHOP_REPORT_PRODUCT_PATTERN_URL.replace(":slug", slug);
}
export const SHOP_SUPPLEMENTS_URL = `${SHOP_URL}/supplements`;
export const SHOP_SUPPLEMENT_PATTERN_URL = "/shop/supplements/:slug";
export function getShopSupplementUrl(slug) {
  return SHOP_SUPPLEMENT_PATTERN_URL.replace(":slug", slug);
}

export const ALL_TESTS_SLUG = "all-tests";
export const TESTS_URL = `/${ALL_TESTS_SLUG}`;
export const TEST_FAMILY_PATTERN_URL = `/${ALL_TESTS_SLUG}/:familySlug`;
export function getTestFamilyUrl(familySlug) {
  return TEST_FAMILY_PATTERN_URL.replace(":familySlug", familySlug);
}
export const TEST_PATTERN_URL = `/${ALL_TESTS_SLUG}/:familySlug/:slug`;
export function getTestUrl(slug, familySlug) {
  return TEST_PATTERN_URL.replace(":slug", slug).replace(":familySlug", familySlug);
}

// Old Tests Redirect
export const CATCH_SHOP_URL = "/shop";
export const OLD_TESTS_URL = "/tests";
export const OLD_TEST_PATTERN_URL = "/tests/:slug";

// Accounts
export const SIGNUP_URL = "/accounts/signup";
export const SIGNUP_DONE_URL = "/accounts/signup/done";
export const SIGNUP_CONFIRM_PATTERN_URL = "/accounts/signup/:userId/:token";
export const SIGNUP_COMPLETE_URL = "/accounts/signup/complete";
export const ACCEPT_PATIENT_INVITE_PATTERN_URL = "/accounts/patient-invite/:patientId/:token";

export const LOGIN_URL = "/accounts/login";
export const LOGGED_OUT_URL = "/accounts/logged-out";

export const ACCEPT_ADMIN_INVITE_PATTERN_URL = "/accounts/admin/invite/:invitationId";
export const ACCEPT_PARTNER_INVITE_PATTERN_URL = "/accounts/partner/invite/:invitationId";
export const ACCEPT_PARTNER_LINK_INVITE_PATTERN_URL = "/accounts/partner/link/:partnerLinkCode";

export const ACCEPT_RESULTS_ACCESS_REQUEST_PATTERN_URL =
  "/accounts/partner/results-access-request/:requestId";

export const REQUEST_PASSWORD_RESET_URL = "/accounts/password-reset";
export const REQUEST_PASSWORD_RESET_DONE = "/accounts/password-reset/email-sent";
export const RESET_PASSWORD_CONFIRM_PATTERN_URL = "/accounts/password-reset/:userId/:token";
export const RESET_PASSWORD_COMPLETE_URL = "/accounts/password-reset/success";

// Profile
export const PLATFORM_PROFILE_WELCOME_URL = "/onboarding/welcome";
export const PLATFORM_PROFILE_FORM_URL = "/onboarding";
export const PLATFORM_PROFILE_DIET_PREFERENCES_URL = "/onboarding/diet-preferences";
export const PLATFORM_PROFILE_GOALS_URL = "/onboarding/goals";

// Legal
export const COOKIE_URL = `/${COOKIE_SLUG}`;
export const PRIVACY_URL = `/${PRIVACY_SLUG}`;
export const TERMS_OF_SERVICE_URL = `/${TERMS_OF_SERVICE_SLUG}`;
export const TERMS_OF_USE_URL = `/${TERMS_OF_USE_SLUG}`;
export const PARTNER_TERMS_URL = `/${PARTNER_TERMS_SLUG}`;
export const SHIPPING_AND_RETURNS_URL = `/${SHIPPING_AND_RETURNS_SLUG}`;

export const REGENERUS_TERMS_AND_CONDITIONS_URL = `/${REGENERUS_TERMS_AND_CONDITIONS_SLUG}`;
export const REGENERUS_PRIVACY_URL = `/${REGENERUS_PRIVACY_SLUG}`;
export const REGENERUS_SHIPPING_URL = `/${REGENERUS_SHIPPING_SLUG}`;
export const REGENERUS_TERMS_OF_USE_URL = `/${REGENERUS_TERMS_OF_USE_SLUG}`;
export const REGENERUS_COOKIE_POLICY_URL = `/${REGENERUS_COOKIE_POLICY_SLUG}`;

// Utils
export const ERROR_404_URL = "/404";
export const ERROR_500_URL = "/500";

export const CMP_ROOT_URL = "/cmp";
export const CMP_WITHOUT_ORG = "/cmp/dashboard";
export const CMP_ORGANISATIONS_ROOT_URL = `${CMP_ROOT_URL}/organisations/:organisationId`;
export const CMP_ORGANISATION_PATIENT_REGISTRATION_URL = `${CMP_ORGANISATIONS_ROOT_URL}/patient-registration/:token`;
export const CMP_ORGANISATION_PATIENT_REGISTRATION_VERIFY_EMAIL_URL = `${CMP_ORGANISATIONS_ROOT_URL}/patient-registration/verify-email`;
export const CMP_DASHBOARD_URL = `${CMP_ORGANISATIONS_ROOT_URL}/dashboard`;
export const CMP_ORDERS_URL = `${CMP_DASHBOARD_URL}/orders/direct`;
export const CMP_ORDER_URL = `${CMP_DASHBOARD_URL}/order/:orderId`;
export const CMP_STOCK_URL = `${CMP_DASHBOARD_URL}/orders/stock`;
export const CMP_BUSINESS_URL = `${CMP_DASHBOARD_URL}/business`;
export const CMP_COMMISSIONS_URL = `${CMP_DASHBOARD_URL}/commissions`;
export const CMP_PATIENTS_URL = `${CMP_DASHBOARD_URL}/patients`;
export const CMP_PATIENTS_MANAGE_PATIENTS_URL = `${CMP_PATIENTS_URL}/manage_patients`;
export const CMP_PATIENTS_CALLBACK_REQUESTS_URL = `${CMP_PATIENTS_URL}/callback_requests`;
export const CMP_FAP_EXPERT_APPLICATION_URL = "https://form.typeform.com/to/xw85nY62";
export const CMP_PATIENTS_NEW_RESULTS_URL = `${CMP_PATIENTS_URL}/new_results`;
export const CMP_SHOP_URL = `${CMP_DASHBOARD_URL}/shop`;
export const CMP_SHOP_TAB_URL = `${CMP_SHOP_URL}/:tab`;
export const CMP_SUPPORT_URL = `${CMP_DASHBOARD_URL}/support`;
export const CMP_SUPPORT_PHLEBOTOMY_ANCHOR = "phlebotomy";
export const CMP_LEARNING_HUB_URL = `${CMP_DASHBOARD_URL}/learning_hub`;
export const CMP_LEARNING_HUB_DASHBOARD_URL = `${CMP_LEARNING_HUB_URL}/dashboard`;
export const CMP_LEARNING_HUB_COURSES_URL = `${CMP_LEARNING_HUB_URL}/courses`;
export const CMP_LEARNING_HUB_COURSE_URL = `${CMP_LEARNING_HUB_COURSES_URL}/:slug`;
export const CMP_LEARNING_HUB_WEBINARS_URL = `${CMP_LEARNING_HUB_URL}/webinars`;
export const CMP_LEARNING_HUB_WEBINAR_URL = `${CMP_LEARNING_HUB_URL}/webinars/:slug`;
export const CMP_LEARNING_HUB_EVENTS_URL = `${CMP_LEARNING_HUB_URL}/events`;
export const CMP_LEARNING_HUB_EVENT_URL = `${CMP_LEARNING_HUB_URL}/events/:slug`;
export const CMP_LEARNING_HUB_VIDEO_GUIDES_URL = `${CMP_LEARNING_HUB_URL}/video_guides`;
export const CMP_LEARNING_HUB_VIDEO_GUIDE_URL = `${CMP_LEARNING_HUB_URL}/video_guides/:slug`;
export const CMP_AFFILIATE_URL = `${CMP_ROOT_URL}/affiliate`;
export const CMP_PRACTITIONER_URL = `${CMP_ROOT_URL}/practitioner`;
export const CMP_STUDENT_APPLICATION_URL = `${CMP_ROOT_URL}/student_application`;
export const CMP_PATIENTS_BASE_URL = `${CMP_ORGANISATIONS_ROOT_URL}/patients/:patientId`;
export const CMP_PATIENTS_PROFILE_URL = `${CMP_PATIENTS_BASE_URL}/profile`;
export const CMP_PATIENTS_DASHBOARD_URL = `${CMP_PATIENTS_BASE_URL}/dashboard`;
export const CMP_PATIENTS_TESTS_URL = `${CMP_PATIENTS_BASE_URL}/tests`;
export const CMP_PATIENTS_TEST_RESULTS_URL = `${CMP_PATIENTS_BASE_URL}/test-results`;
export const CMP_PATIENTS_TEST_RESULT_URL = `${CMP_PATIENTS_BASE_URL}/test-results/:userTestId`;
export const CMP_PATIENTS_TEST_URL = `${CMP_PATIENTS_BASE_URL}/tests/:userTestId`;
export const CMP_PATIENTS_REPORTS_URL = `${CMP_PATIENTS_BASE_URL}/reports`;
export const CMP_PATIENTS_REPORT_URL = `${CMP_PATIENTS_REPORTS_URL}/:reportSlug`;
export const CMP_PATIENTS_REPORT_PRINT_URL = `${CMP_PATIENTS_REPORT_URL}/print`;
export const CMP_PATIENTS_REPORT_TAB_URL = `${CMP_PATIENTS_REPORT_URL}/:tab`;
export const CMP_PATIENTS_WELLNESS_URL = `${CMP_PATIENTS_BASE_URL}/wellness`;
export const CMP_PATIENTS_QUESTIONNAIRE_URL = `${CMP_PATIENTS_BASE_URL}/questionnaire`;
export const CMP_PATIENTS_SUBSECTOR_URL = `${CMP_PATIENTS_WELLNESS_URL}/:subsectorSlug`;
export const CMP_PATIENTS_SELF_ASSESSMENT_URL = `${CMP_PATIENTS_BASE_URL}/self_assessment`;
export const CMP_PATIENTS_SUGGESTIONS_URL = `${CMP_PATIENTS_BASE_URL}/suggestions`;
export const CMP_PATIENTS_SUGGESTION_URL = `${CMP_PATIENTS_SUGGESTIONS_URL}/:recommendationId`;
export const CMP_PATIENTS_FOOD_LIST_URL = `${CMP_PATIENTS_BASE_URL}/food_list`;
export const CMP_PATIENTS_USER_RESULT_URL = `${CMP_PATIENTS_BASE_URL}/result/:userResultId`;
export const CMP_PATIENTS_THEME_SUBSECTOR_URL = `${CMP_PATIENTS_BASE_URL}/theme/:themeSlug/:subsectorSlug`;
export const CMP_PATIENTS_USER_DATAPOINT_URL = `${CMP_PATIENTS_BASE_URL}/biomarker/:userDatapointId`;

export const CMP_PROFILE_URL = `${CMP_DASHBOARD_URL}/profile`;
export const CMP_SIGNIN_URL = `${CMP_ROOT_URL}/login`;
export const CMP_BUSINESS_TAB_URL = `${CMP_BUSINESS_URL}/:tab`;
export const CMP_CHECKOUT_URL = `${CMP_DASHBOARD_URL}/checkout`;
export const CMP_AFFILIATE_LINKS = `${CMP_DASHBOARD_URL}/affiliate_links`;

export const CMP_REQUEST_PASSWORD_RESET_URL = "/cmp/password-reset";
export const CMP_REQUEST_PASSWORD_RESET_DONE = "/cmp/password-reset/email-sent";
export const CMP_RESET_PASSWORD_CONFIRM_PATTERN_URL = "/cmp/password-reset/:userId/:token";
export const CMP_RESET_PASSWORD_COMPLETE_URL = "/cmp/password-reset/success";

function substituteUrlParams(urlTemplate, kwargs) {
  let url = urlTemplate;

  for (const key in kwargs) {
    url = url.replace(":" + key, kwargs[key]);
  }

  return url;
}

export const getCmpUrl = (urlTemplate, kwargs) => substituteUrlParams(urlTemplate, kwargs);

// Guardians looking at their wards
// may be we ought to change `patients` in the url to `wards`
export const WARD_BASE_URL = "/patients/:patientId";
export const WARDS_PROFILE_URL = `${WARD_BASE_URL}/profile`;
export const WARDS_TESTS_URL = `${WARD_BASE_URL}/tests`;
export const WARD_TEST_URL = `${WARDS_TESTS_URL}/:userTestId`;
export const WARDS_ORDER_URL = `${WARD_BASE_URL}/orders/:orderId`;
export const WARD_TAB_URL = `${WARD_BASE_URL}/:tab`;
export const WARD_SUBSECTOR_URL = `${WARD_BASE_URL}/wellness/:subsectorSlug`;
export const WARD_USER_RESULT_URL = `${WARD_BASE_URL}/:userResultId`;
export const WARD_FOOD_LIST_URL = `${WARD_BASE_URL}/food_list`;
export const WARD_SUGGESTIONS_URL = `${WARD_BASE_URL}/suggestions`;
export const WARD_SUGGESTION_URL = `${WARD_SUGGESTIONS_URL}/:recommendationId`;
export const WARD_THEME_SUBSECTOR_URL = `${WARD_BASE_URL}/theme/:themeSlug/:subsectorSlug`;
export const WARD_USER_DATAPOINT_URL = `${WARD_BASE_URL}/:userDatapointId`;

export const getWardUrl = (urlTemplate, kwargs) => substituteUrlParams(urlTemplate, kwargs);

// Partners
export const PARTNERS_URL = "/partners";

// Old Patients
// Now guardians looking at wards
export const PATIENT_URL = "/patients/:patientId";

// Hijack

export const RELEASE_IMPERSONATION_URL = `${getAPIBase()}/hijack/release/`;

// TPO

export const TPO_TEST_URL = "/tpo";

export const THEME_URL_BASE = "/theme";
export const THEME_URL = `${THEME_URL_BASE}/:themeSlug`;
export const THEME_SECTOR_URL = `${THEME_URL}/:sectorSlug`;
export const THEME_SUBSECTOR_URL = `${THEME_URL}/:subsectorSlug`;

export function getThemeUrl(themeSlug) {
  return THEME_URL.replace(":themeSlug", themeSlug);
}

export function getTPOSectorUrl(themeSlug, sectorSlug) {
  return THEME_SECTOR_URL.replace(":themeSlug", themeSlug).replace(":sectorSlug", sectorSlug);
}

export function getTPOSubsectorUrl(themeSlug, subsectorSlug) {
  return THEME_SUBSECTOR_URL.replace(":themeSlug", themeSlug).replace(
    ":subsectorSlug",
    subsectorSlug
  );
}

export const TEST_RESULTS_URL_BASE = "/test-results";

export const TEST_RESULTS_URL = `${TEST_RESULTS_URL_BASE}/:userTestId`;

export function getTestResultsUrl(userTestId) {
  return TEST_RESULTS_URL.replace(":userTestId", userTestId);
}

export const TEST_RESULT_URL_BASE = "/test-result";

export const TEST_RESULT_URL = `${TEST_RESULT_URL_BASE}/:userResultId`;

// the term "biomarker" is for the user
// internally we use the term "datapoint"
export const TEST_DATAPOINT_URL_BASE = "/test-biomarker";
export const TEST_DATAPOINT_URL = `${TEST_DATAPOINT_URL_BASE}/:userDatapointId`;

export function getTestDatapointUrl(userDatapointId) {
  return TEST_DATAPOINT_URL.replace(":userDatapointId", userDatapointId);
}

export function getTestResultUrl(userResultId) {
  return TEST_RESULT_URL.replace(":userResultId", userResultId);
}

export const RECOMMENDATIONS_URL = "/suggestions";

export const RECOMMENDATION_URL = `${RECOMMENDATIONS_URL}/:recommendationId`;

export function getTPORecommendationUrl(recommendationId) {
  return RECOMMENDATION_URL.replace(":recommendationId", recommendationId);
}

export const SUPPLEMENT_URL = `${SUPPLEMENTS_URL}/:slug`;

export function getTPOSupplementUrl(slug) {
  return SUPPLEMENT_URL.replace(":slug", slug);
}

export const FOOD_URL = `${FOODS_URL}/:foodId`;
export function getTPOFoodUrl(foodId) {
  // return FOOD_URL.replace(":foodId", foodId);
  // is there a particular page for a food item?
  return FOODS_URL;
}
